<template>
  <div class="ai-assistant-input-container">
    <v-divider />
    <div class="pa-6">
      <!-- Chat Limit Notification (only for chat view when session is inactive) -->
      <div v-if="isSessionInactive" class="align-center">
        <p class="body3">
          {{ $t("aiAssistant.chat.sessionEnded") }}
        </p>
        <v-btn variant="elevated" color="primary" size="large" block @click="onStartNewChat">
          <v-icon size="20" icon="$newMessage" class="mr-2" />
          {{ $t("aiAssistant.chat.startNewChat") }}
        </v-btn>
      </div>

      <!-- Normal Input -->
      <div v-else>
        <v-text-field
          v-model="inputMessage"
          :placeholder="placeholder"
          :aria-label="placeholder"
          :disabled="disabled"
          variant="outlined"
          density="compact"
          hide-details
          class="ai-ask-input"
          :maxlength="maxLength"
          @keyup.enter="handleSendMessage"
          @paste="handlePaste"
        >
          <template #append-inner>
            <div
              class="icon-background d-flex align-center justify-center"
              @click="handleSendMessage"
            >
              <v-icon icon="$inputFrame" />
            </div>
          </template>
        </v-text-field>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";
import { MAX_MESSAGE_LENGTH } from "@/_store/ai-assistant.module";

export default defineComponent({
  props: {
    placeholder: {
      type: String,
      default: "",
    },
    isSessionInactive: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue", "send-message", "start-new-chat"],
  setup(props, { emit }) {
    const inputMessage = ref(props.modelValue);

    // Watch for external changes to modelValue
    watch(
      () => props.modelValue,
      (newValue) => {
        inputMessage.value = newValue;
      }
    );

    // Watch for internal changes and emit update
    watch(inputMessage, (newValue) => {
      emit("update:modelValue", newValue);
    });

    const maxLength = MAX_MESSAGE_LENGTH;

    const handleSendMessage = () => {
      if (props.disabled) return; // Don't send if disabled
      if (!inputMessage.value.trim()) return;
      if (inputMessage.value.length > maxLength) return; // Prevent sending if over limit

      const message = inputMessage.value.trim().replace(/\n/g, ""); // Strip newlines
      inputMessage.value = "";
      emit("send-message", message);
    };

    const handlePaste = (event: ClipboardEvent) => {
      event.preventDefault();
      const pastedText = event.clipboardData?.getData("text") || "";
      const currentText = inputMessage.value;

      // Get the current selection to replace it
      const target = event.target as HTMLInputElement;
      const start = target.selectionStart || 0;
      const end = target.selectionEnd || 0;

      // Calculate what the new text would be
      const newText = currentText.slice(0, start) + pastedText + currentText.slice(end);

      // Truncate to max length if needed
      if (newText.length > maxLength) {
        inputMessage.value = newText.slice(0, maxLength);
      } else {
        inputMessage.value = newText;
      }
    };

    const onStartNewChat = () => {
      emit("start-new-chat");
    };

    return {
      inputMessage,
      handleSendMessage,
      handlePaste,
      onStartNewChat,
      maxLength,
    };
  },
});
</script>

<style lang="scss" scoped>
.ai-assistant-input-container {
  position: sticky;
  bottom: 0;
  background-color: rgb(var(--v-theme-white));
  border-radius: 0 0 6px 6px;
  z-index: 1;
}

.ai-ask-input :deep(.v-field) {
  background-color: rgb(var(--v-theme-indigo-faint));
}

.icon-background {
  background-color: rgb(var(--v-theme-white));
  border-radius: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.ai-ask-input:focus-within .icon-input-frame:before {
  color: rgb(var(--v-theme-indigo-base));
}
</style>
